<template>
  <v-container fluid :class="`account-details-wrapper ${$vuetify.breakpoint.smAndUp?'':'pl-5'}`">
    <v-form
      v-model="isFormValid"
      ref="accountForm"
      @submit.prevent="saveAccountsInfo"
    >
    <v-row class="mt-6">
      <v-col cols="12" sm="6" md="6" class="pa-0">
        <h3>Account Information</h3>
      </v-col>
    </v-row>
    <v-row class="mt-6">
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0"
      >
        <p>
          <span>Account ID</span>
          <span class="ml-10">{{ accountId }}</span>
        </p>
      </v-col>
      <v-col
        v-if="isDefaultAccount"
        cols="12"
        sm="6"
        md="6"
        :class="$vuetify.breakpoint.smAndUp ? '':'ml-0 pl-0 pr-9 mr-0'"
      >
        <p>
          <span>Account Type</span>
          <span class="ml-10">
            <v-chip color="#03b6fc">
              <span class="white--text">
                Default
              </span>              
            </v-chip>
          </span>
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9 mr-0"
      >
        <v-text-field
          v-track="'accounts-profile-name'"
          id="accounts-profile-name"
          v-model.trim="accountsProfile.name"
          label="Account Name"
          outlined
          required
          persistent-hint
          disabled
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
      <div class="ml-n6 mt-6">
        <v-tooltip
          bottom
          color="rgba(0, 0, 0, 0.9)"
          max-width="340"
          attach
        >
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <span
            attach
            class="caption"
          >
            If you need to make changes to your account name, please contact your Customer Success Manager.
          </span>
        </v-tooltip>
      </div>
      <v-col cols="12" sm="6" md="6" :class="$vuetify.breakpoint.smAndUp ? 'pr-7':'ml-0 pl-0 pr-9 mr-0'">
        <v-text-field
          v-track="'accounts-client-name'"
          id="accounts-client-name"
          v-model.trim="accountsProfile.client.name"
          label="Client Name"
          outlined
          required
          persistent-hint
          disabled
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9 mr-0"
      >
        <v-autocomplete
          v-track="'accounts-industry'"
          id="accounts-industry"
          v-model="accountsProfile.industry_id"
          attach
          outlined
          label="Industry"
          item-text="name"
          item-value="id"
          item-color="#03B6FC"
          class="custom-selectbox"
          :items="industriesList"
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" :class="$vuetify.breakpoint.smAndUp ? 'pr-7':'ml-0 pl-0 pr-9 mr-0'">
        <v-autocomplete
          v-track="'accounts-language'"
          id="accounts-language"
          v-model="accountsProfile.language"
          attach
          outlined
          label="Language"
          item-text="name"
          item-value="value"
          item-color="#03B6FC"
          class="custom-selectbox"
          :items="languageList"
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9 mr-0"
      >
        <v-text-field
          v-track="'accounts-website'"
          id="accounts-website"
          v-model.trim="accountsProfile.website"
          label="Website"
          outlined
          required
          persistent-hint
          :disabled="checkRole('accounts_read_only')"
          :rules="[rules.required]"
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
    </v-row>
    
    <v-row class="mt-6">
      <h3>Address</h3>
    </v-row>   
    <v-row>
      <div :class="$vuetify.breakpoint.smAndUp ? 'mt-0 text--secondary':'mt-0 text--secondary pr-9'">
        This address will be available to use as part of your email footer. This will not overwrite your current address within footer management. To change your footer email please adjust within footer management.
      </div>
    </v-row> 
    <v-row class="mt-6">
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9"
      >
        <v-text-field
          v-track="'accounts-profile-address1'"
          id="accounts-profile-address1"
          v-model.trim="accountsProfile.address1"
          label="Address Line 1"
          outlined
          required
          persistent-hint
          :disabled="checkRole('accounts_read_only')"
          :rules="[rules.required]"
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" :class="$vuetify.breakpoint.smAndUp ? 'pr-7':'ml-0 pl-0 pr-9'">
        <v-text-field
          v-track="'accounts-profile-address2'"
          id="accounts-profile-address2"
          v-model.trim="accountsProfile.address2"
          label="Address Line 2"
          outlined
          required
          persistent-hint
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9"
      >
        <v-autocomplete
          v-track="'accounts-profile-country'"
          id="accounts-profile-country"
          v-model="country"
          attach
          outlined
          label="Country"
          item-text="name"
          item-value="value"
          item-color="#03B6FC"
          class="custom-selectbox"
          :items="countriesList"
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true; onCountryChange = true;"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" :class="$vuetify.breakpoint.smAndUp ? 'pr-7':'ml-0 pl-0 pr-9'">
        <v-autocomplete
          v-track="'accounts-profile-state'"
          id="accounts-profile-state"
          v-model="state"
          attach
          outlined
          label="State/Province"
          item-text="name"
          item-value="value"
          item-color="#03B6FC"
          class="custom-selectbox"
          :items="statesList"
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
          :rules="[rules.required]"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="6"
        class="ml-0 pl-0 pr-9"
      >
        <v-text-field
          v-track="'accounts-profile-city'"
          id="accounts-profile-city"
          v-model.trim="accountsProfile.city"
          label="City"
          outlined
          required
          persistent-hint
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" :class="$vuetify.breakpoint.smAndUp ? 'pr-7':'ml-0 pl-0 pr-9'">
        <v-text-field
          v-track="'accounts-profile-zipcode'"
          id="accounts-profile-zipcode"
          v-model.trim="accountsProfile.zip"
          label="Zip/Postal Code"
          outlined
          required
          persistent-hint
          :disabled="checkRole('accounts_read_only')"
          @input="enableSaveAccountsProfile = true"
          :rules="[rules.required]"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-btn
        
        v-if="!checkRole('accounts_read_only')"
        :dark="!isFormDisabled"
        :disabled="isFormDisabled"
        type="submit"
        id="save-accounts-information"
        v-track="'save-accounts-information'"
      >
        SAVE CHANGES
      </v-btn>
    </v-row>
    </v-form>
  </v-container>
</template>
<script>
  import { $selfService } from "@core/services";
  import { validationMixin } from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'
  import { mapGetters } from 'vuex'
  import constants from "@core/config/constants.js";
  export default {
    name: 'AccountSettings',
    mixins: [validationMixin],
    data () {
      return {
        onCountryChange : false,
        accountId: '',
        enableSaveAccountsProfile: false,
        isFormValid: false,
        accountsProfile: {
          id: '',
          name: '',
          language: '',
          website: '',
          address1: '',
          address2: '',
          city: '',
          zip: '',
          accountId: '',
          client: {
            id: '',
            name: '',
          },
          country:{}
        },
        industry: {},
        industriesList: [],
        country: {},
        countriesList: [],
        state: {},
        statesList: [],
        languageValue: {},
        languageList: constants.LANGAUGE_LIST,
        loading: false,
        rules: {
          required: value => !!value || 'Required.',
        },
      }
    },
    computed: {
      ...mapGetters({ 
        checkRole: 'auth/checkRole',
        userdata: 'auth/userdata'
      }),
      /**
       * Check current account is default account 
       * @return {Boolean}
       */
      isDefaultAccount(){
        return (parseInt(this.userdata.default_account_id)) === this.accountId
      },
      isFormDisabled(){
        return !this.isFormValid  || !this.enableSaveAccountsProfile
      }, 

    },
    watch: {
      'country': function() {
        if (this.country) {
          this.getStatesList()
        }
      },
    },
    async mounted () {
      if (this.$store.state.auth.editAccountId) {
        this.accountId = this.$store.state.auth.editAccountId
      } else {
        this.accountId = this.$store.state.auth.account.id
      }      
      this.fetchData();
    },
    methods: {
      /**
     * Fetches all the required data from api resources
     * @description Stops the execution once all the promises are resolved
     */
    async fetchData() {
        this.loading = true;
        
        const resources = [
          this.fetchAccountsInfo(),
          this.getIndustriesList(),
          this.getCountriesList()
        ];
        await Promise.all(resources);
        this.loading =false;
      },
      getIndustriesList(){
        $selfService.getIndustriesList().then(response => {
          this.industriesList = response.data.data.industries
          }).catch(error => {
            this.$store.commit('ui/snackbar', { message: error.response.data.error, type: 'error', timeout: 3000 })
        })
      },
      getCountriesList(){
        $selfService.getCountriesList().then(response => {
          this.countriesList = response.data.data.countries
          this.country = this.accountsProfile.country;
          }).catch(error => {
          this.$store.commit('ui/snackbar', { message: error.response.data.error, type: 'error', timeout: 3000 })
        })
      },
      getStatesList(){
        this.loading = true
        $selfService.getStatesList(this.country).then(response => {
          this.statesList = response.data.data.states
          if(!this.onCountryChange){
            this.state = this.accountsProfile.state
          }
          else{
            this.state = ''
          }
        }).finally(() => {
          this.loading = false
        })
      },
      fetchAccountsInfo () {
        this.loading = true
        $selfService.getAccountsData(this.accountId).then(response => {
          this.accountsProfile = response.data.data.account

          if (response.data.data.account.client === undefined) {
            this.accountsProfile.client = {
              name: '',
              id: '',
            }
          }
                           
        }).finally(() => {
          this.loading = false
        })        
      },
      saveAccountsInfo () {
        
        if(this.checkRole('accounts_read_only') || !this.isFormValid){
          return this.$refs.accountForm.validate();
        }
        // If form is valid ..
        this.loading = true
        const payloadData = {
          accountId: this.accountsProfile.id,
          id: this.accountsProfile.id,
          account: {
            name: this.accountsProfile.name,
            language: this.accountsProfile.language,
            industry_id: this.accountsProfile.industry_id,
            website: this.accountsProfile.website,
            address1: this.accountsProfile.address1,
            address2: this.accountsProfile.address2,
            city: this.accountsProfile.city,
            state: this.state,
            country: this.country,
            zip: this.accountsProfile.zip,
          },
        }
        $selfService.saveAccountsData(this.accountId, payloadData).then(() => {
          this.$store.commit('ui/snackbar', { 
            message: 'Account data updated successfully', 
            type: 'success' 
          })
          this.fetchAccountsInfo()
          this.$store.dispatch('auth/getAccountInfo')
        }).finally(() => {
          this.loading = false            
        })          
        
      },
    },
  }
</script>